import { useEffect, useRef } from 'react';
import {
  isFreshMatch,
  NAMES_INDEX_KEY,
  type Names,
  NO_NAMES,
  namesKey,
  parseNames,
  touchNamesIndex,
} from '@/game/view/names';
import type { UiStore } from './store';

/**
 * This match's names from localStorage. The seed defaults to the room id, so
 * a rematch in the same room has the same key: a match that has only just
 * begun (`tick`) clears what's stored instead, while a host reload mid-match
 * restores it (a follower restarts from tick 0 until its first snapshot, so
 * its names clear). Storage may be unavailable (private mode): none then.
 */
export function loadNames(key: string, tick: number): Names {
  try {
    if (isFreshMatch(tick)) {
      localStorage.removeItem(key);
      return NO_NAMES;
    }
    return parseNames(localStorage.getItem(key));
  } catch {
    return NO_NAMES;
  }
}

/** Stores `names` under `key` and drops the name sets of all but the most recent matches. */
export function saveNames(key: string, names: Names) {
  try {
    if (Object.keys(names).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(names));
    const { index, drop } = touchNamesIndex(localStorage.getItem(NAMES_INDEX_KEY), key, Date.now());
    for (const old of drop) localStorage.removeItem(old);
    localStorage.setItem(NAMES_INDEX_KEY, JSON.stringify(index));
  } catch {
    // Private mode / blocked storage: keep the in-memory names only.
  }
}

/**
 * Keeps the store's `names` in localStorage for this match and player
 * (`namesKey`): loaded during the first render (before any panel reads the
 * store, so no flash of default labels) and saved on every change. Nothing is
 * stored without a seed.
 */
export function usePersistedNames(ui: UiStore, seed: string, playerId: string, tick: number) {
  const key = seed === '' ? null : namesKey(seed, playerId);
  const loaded = useRef<string | null>(null);
  if (key !== null && loaded.current !== key) {
    loaded.current = key;
    ui.set({ names: loadNames(key, tick) });
  }
  useEffect(() => {
    if (key === null) return;
    let saved = ui.get().names;
    return ui.subscribe(() => {
      const { names } = ui.get();
      if (names === saved) return;
      saved = names;
      saveNames(key, names);
    });
  }, [ui, key]);
}
